import React, { useEffect, useState } from 'react';
import {
  Box, Typography, TextField, Button, Paper, IconButton, Snackbar, Alert,
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import RestoreIcon from '@mui/icons-material/Restore';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import HandshakeIcon from '@mui/icons-material/Handshake';
import { adminFetch } from '../../lib/adminAuth';

const DEFAULTS = {
  heroHeading: 'Collaborations',
  heroBadge: 'New Feature',
  heroDescription: 'Collaborate with other businesses around you and offer their customers a promotion when they earn a reward. Using this feature will help you expose yourself to other potential customers in your area.',
  heroChip: 'You can only collaborate with businesses in different categories to you',
  benefitsHeading: 'How It Works',
  benefits: [
    { title: 'Increase Your Visibility', description: 'Get your business in front of new customers who are already engaged with loyalty programs in your area.' },
    { title: 'Reach New Customers', description: 'When a customer earns a reward at a partner business, they see your promotion too. Tap into an engaged audience.' },
    { title: 'Cross-Promote Offers', description: 'Offer exclusive promotions to customers from collaborating businesses. A coffee shop pairs with a bakery, a gym pairs with a smoothie bar.' },
    { title: 'Grow Together', description: 'Build a network of complementary businesses in your area. More collaborations means more exposure for everyone involved.' },
  ],
  stepsHeading: 'What Happens When You Sign Up',
  steps: [
    'Your business becomes visible to other businesses in your area looking to collaborate.',
    'Businesses in different categories can send you a collaboration request.',
    'When you accept, their customers will see your promotion when they earn a reward - and vice versa.',
    'You set the promotion you want to offer to their customers.',
    'Track new customers coming from collaborations right from your dashboard.',
  ],
  ctaHeading: 'Ready to grow your customer base?',
  ctaSubtext: "Sign up to start collaborating with businesses near you. It's free and you can opt out at any time.",
  ctaButton: 'Sign Up for Collaborations',
  successTitle: "You're signed up for Collaborations!",
  successText: "We'll notify you when businesses in your area are available to collaborate with. Stay tuned!",
};

export default function AdminCollaborations() {
  const [content, setContent] = useState(DEFAULTS);
  const [saving, setSaving] = useState(false);
  const [snack, setSnack] = useState({ open: false, message: '', severity: 'success' });

  useEffect(() => {
    (async () => {
      try {
        const res = await adminFetch('/api/admin/collaborations-content');
        const data = await res.json();
        if (data && Object.keys(data).length > 1) {
          setContent({
            heroHeading: data.heroHeading || DEFAULTS.heroHeading,
            heroBadge: data.heroBadge || DEFAULTS.heroBadge,
            heroDescription: data.heroDescription || DEFAULTS.heroDescription,
            heroChip: data.heroChip || DEFAULTS.heroChip,
            benefitsHeading: data.benefitsHeading || DEFAULTS.benefitsHeading,
            benefits: data.benefits && data.benefits.length > 0 ? data.benefits : DEFAULTS.benefits,
            stepsHeading: data.stepsHeading || DEFAULTS.stepsHeading,
            steps: data.steps && data.steps.length > 0 ? data.steps : DEFAULTS.steps,
            ctaHeading: data.ctaHeading || DEFAULTS.ctaHeading,
            ctaSubtext: data.ctaSubtext || DEFAULTS.ctaSubtext,
            ctaButton: data.ctaButton || DEFAULTS.ctaButton,
            successTitle: data.successTitle || DEFAULTS.successTitle,
            successText: data.successText || DEFAULTS.successText,
          });
        }
      } catch (err) {
        console.error(err);
      }
    })();
  }, []);

  const updateField = (field, value) => setContent(prev => ({ ...prev, [field]: value }));

  const updateBenefit = (index, field, value) => {
    setContent(prev => {
      const benefits = [...prev.benefits];
      benefits[index] = { ...benefits[index], [field]: value };
      return { ...prev, benefits };
    });
  };

  const addBenefit = () => {
    setContent(prev => ({ ...prev, benefits: [...prev.benefits, { title: '', description: '' }] }));
  };

  const removeBenefit = (index) => {
    setContent(prev => ({ ...prev, benefits: prev.benefits.filter((_, i) => i !== index) }));
  };

  const updateStep = (index, value) => {
    setContent(prev => {
      const steps = [...prev.steps];
      steps[index] = value;
      return { ...prev, steps };
    });
  };

  const addStep = () => {
    setContent(prev => ({ ...prev, steps: [...prev.steps, ''] }));
  };

  const removeStep = (index) => {
    setContent(prev => ({ ...prev, steps: prev.steps.filter((_, i) => i !== index) }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await adminFetch('/api/admin/collaborations-content', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(content),
      });
      if (res.ok) setSnack({ open: true, message: 'Collaborations content saved!', severity: 'success' });
      else throw new Error();
    } catch {
      setSnack({ open: true, message: 'Failed to save', severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    setContent(DEFAULTS);
    setSnack({ open: true, message: 'Reset to defaults (save to apply)', severity: 'info' });
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 4 }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 700, color: '#1a1a2e' }}>Collaborations Page</Typography>
          <Typography variant="body2" sx={{ color: '#888' }}>Manage the Collaborations page content for vendors</Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1.5 }}>
          <Button startIcon={<RestoreIcon />} onClick={handleReset} sx={{ textTransform: 'none', borderRadius: 2, color: '#888' }}>Reset</Button>
          <Button variant="contained" startIcon={<SaveIcon />} onClick={handleSave} disabled={saving}
            sx={{ textTransform: 'none', borderRadius: 2, bgcolor: '#1a1a2e', color: '#fff', '&:hover': { bgcolor: '#16213e' } }}>
            {saving ? 'Saving...' : 'Save All'}
          </Button>
        </Box>
      </Box>

      {/* Hero Section */}
      <Paper sx={{ p: 3, borderRadius: 3, mb: 3 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>Hero Section</Typography>
        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, mb: 2 }}>
          <TextField label="Heading" value={content.heroHeading} onChange={e => updateField('heroHeading', e.target.value)} size="small" />
          <TextField label="Badge" value={content.heroBadge} onChange={e => updateField('heroBadge', e.target.value)} size="small" />
        </Box>
        <TextField label="Description" fullWidth multiline rows={3} value={content.heroDescription} onChange={e => updateField('heroDescription', e.target.value)} sx={{ mb: 2 }} size="small" />
        <TextField label="Info Chip" fullWidth value={content.heroChip} onChange={e => updateField('heroChip', e.target.value)} size="small" />
      </Paper>

      {/* Benefits */}
      <Paper sx={{ p: 3, borderRadius: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <HandshakeIcon sx={{ color: '#ffb71b' }} />
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>{content.benefitsHeading}</Typography>
          </Box>
          <Button startIcon={<AddIcon />} onClick={addBenefit} size="small" sx={{ textTransform: 'none', borderRadius: 2 }}>Add Benefit</Button>
        </Box>
        <TextField label="Benefits Section Heading" fullWidth value={content.benefitsHeading} onChange={e => updateField('benefitsHeading', e.target.value)} sx={{ mb: 2 }} size="small" />

        {content.benefits.map((b, index) => (
          <Box key={index} sx={{ mb: 2, p: 2, border: '1px solid #e8e8e8', borderRadius: 2, bgcolor: '#fafafa' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="caption" sx={{ color: '#999', fontWeight: 600 }}>Benefit #{index + 1}</Typography>
              <IconButton size="small" onClick={() => removeBenefit(index)} sx={{ color: '#ccc', '&:hover': { color: '#e53935' } }}>
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Box>
            <TextField label="Title" fullWidth value={b.title} onChange={e => updateBenefit(index, 'title', e.target.value)} sx={{ mb: 1.5 }} size="small" />
            <TextField label="Description" fullWidth multiline rows={2} value={b.description} onChange={e => updateBenefit(index, 'description', e.target.value)} size="small" />
          </Box>
        ))}
      </Paper>

      {/* Steps */}
      <Paper sx={{ p: 3, borderRadius: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>{content.stepsHeading}</Typography>
          <Button startIcon={<AddIcon />} onClick={addStep} size="small" sx={{ textTransform: 'none', borderRadius: 2 }}>Add Step</Button>
        </Box>
        <TextField label="Steps Section Heading" fullWidth value={content.stepsHeading} onChange={e => updateField('stepsHeading', e.target.value)} sx={{ mb: 2 }} size="small" />

        {content.steps.map((step, index) => (
          <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
            <Box sx={{ width: 24, height: 24, borderRadius: '50%', bgcolor: '#fff8e7', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <Typography variant="caption" sx={{ fontWeight: 700, color: '#ffb71b' }}>{index + 1}</Typography>
            </Box>
            <TextField value={step} onChange={e => updateStep(index, e.target.value)} size="small" fullWidth />
            <IconButton size="small" onClick={() => removeStep(index)} sx={{ color: '#ccc', '&:hover': { color: '#e53935' } }}>
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Box>
        ))}
      </Paper>

      {/* CTA & Success */}
      <Paper sx={{ p: 3, borderRadius: 3 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>Call to Action & Success Messages</Typography>
        <TextField label="CTA Heading" fullWidth value={content.ctaHeading} onChange={e => updateField('ctaHeading', e.target.value)} sx={{ mb: 2 }} size="small" />
        <TextField label="CTA Subtext" fullWidth multiline rows={2} value={content.ctaSubtext} onChange={e => updateField('ctaSubtext', e.target.value)} sx={{ mb: 2 }} size="small" />
        <TextField label="CTA Button Text" fullWidth value={content.ctaButton} onChange={e => updateField('ctaButton', e.target.value)} sx={{ mb: 2 }} size="small" />
        <TextField label="Success Title" fullWidth value={content.successTitle} onChange={e => updateField('successTitle', e.target.value)} sx={{ mb: 2 }} size="small" />
        <TextField label="Success Text" fullWidth value={content.successText} onChange={e => updateField('successText', e.target.value)} size="small" />
      </Paper>

      <Snackbar open={snack.open} autoHideDuration={3000} onClose={() => setSnack(s => ({ ...s, open: false }))} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert severity={snack.severity} onClose={() => setSnack(s => ({ ...s, open: false }))} sx={{ borderRadius: 2 }}>{snack.message}</Alert>
      </Snackbar>
    </Box>
  );
}
